import React, { useMemo, useState } from 'react';
import { translate } from 'components/Translation';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { HeadingMBold, BodyM, HeadingSBold } from 'components/Typography/Typography';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import PaymentTypeForm from 'views/BillingPaymentTrack/subcomponents/tabs/PaymentTypeForm';
import UploadExcelFile from 'views/BillingPaymentTrack/subcomponents/tabs/UploadExcelFile';
import styles from 'assets/jss/material-dashboard-pro-react/views/billingPaymentTrack/billingPaymentTrackStyle';
import tabsContainerStyle from 'assets/jss/material-dashboard-pro-react/layouts/tabsContainerStyle';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Button from 'components/CustomButtons/Button';
import { useHistory } from 'react-router';
import moment from 'moment';
import { BillingPaymentTrackService } from 'services/billingPaymentTrack/billingPaymentTrack';
import { EXCEL_FILE, PAYMENT_TYPE, PROOF_FILES, URL_PATHS } from 'views/BillingPaymentTrack/commons/constants';
import CreateOfflinePaymentStatusDialog from 'views/BillingPaymentTrack/subcomponents/CreateOfflinePaymentStatusDialog';
import { Subscription } from 'rxjs';
import UploadFiles from 'components/UploadFiles/UploadFiles';
import PayWithBOMBanner from './subcomponents/PayWithBOMBanner';
import { useSelector } from 'react-redux';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isBOMOn } from 'utils/killswitch';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyle = makeStyles(styles);
// @ts-ignore
const useTabsContainerStyle = makeStyles(tabsContainerStyle);

enum STEPS {
	PAYMENT_TYPE_FORM = 0,
	UPLOAD_EXCEL_FILE = 1,
	UPLOAD_PROOF_FILES = 2,
}

export enum CREATE_OFFLINE_PAYMENT_STATUS {
	NONE = 0,
	LOADING = 1,
	DONE = 2,
	ERROR = 3,
}

interface IBillingPaymentTrackProps {
	match: {
		path: string;
	};
	refreshPayments: () => Subscription;
}

interface IPaymentDetails {
	invoiceType: string;
	paymentType: string;
	bankCode: string;
	bankAccountNumber: string;
	//TODO: remove when API no longer needs it
	bankTransactionId: string;
	chequeNumber: string;
	paymentDate: moment.Moment;
}

const RegisteringNewLoyalty = ({ match, refreshPayments }: IBillingPaymentTrackProps) => {
	const [activeTab, setActiveTab] = useState<number>(STEPS.PAYMENT_TYPE_FORM);
	const [paymentDetails, setPaymentDetails] = useState<IPaymentDetails>({
		invoiceType: '',
		paymentType: PAYMENT_TYPE.BANK_TRANSFER,
		bankCode: '',
		bankAccountNumber: '',
		bankTransactionId: '123',
		chequeNumber: '',
		paymentDate: moment().startOf('day'),
	});
	const [excelFileFormData, setExcelFileFFormData] = useState<FormData>(new FormData());
	const [proofFilesFormData, setProofFilesFormData] = useState<FormData>(new FormData());
	const [createOfflinePaymentStatus, setCreateOfflinePaymentStatus] = useState<CREATE_OFFLINE_PAYMENT_STATUS>(
		CREATE_OFFLINE_PAYMENT_STATUS.NONE
	);
	const [ticketExternalId, setTicketExternalId] = useState('');
	const classes = useStyle();
	const tabsContainerClasses = useTabsContainerStyle();
	const history = useHistory();

	const remoteConfig = useSelector(remoteConfigSelector);
	// kill switch to check if Bank Muscat are to be shown
	const isBankMuscatEnabled = isBOMOn(remoteConfig);

	const createOfflinePayment = () => {
		// TODO add rest of the fields to jsonData object
		setCreateOfflinePaymentStatus(CREATE_OFFLINE_PAYMENT_STATUS.LOADING);
		const jsonData = new Blob(
			[
				JSON.stringify({
					paymentLevel: paymentDetails.invoiceType,
					paymentType: paymentDetails.paymentType,
					customerType: 'ENTERPRISE',
					bankCode: paymentDetails.bankCode,
					bankAccountNumber: paymentDetails.bankAccountNumber,
					bankTransactionId: paymentDetails.bankTransactionId,
					chequeNumber: paymentDetails.chequeNumber,
				}),
			],
			{
				type: 'application/json',
			}
		);
		const newFormData = new FormData();
		newFormData.append('jsonData', jsonData);
		for (const formDataPair of excelFileFormData.entries()) {
			newFormData.append(...formDataPair);
		}
		for (const formDataPair of proofFilesFormData.entries()) {
			newFormData.append(...formDataPair);
		}
		// TODO parse response
		BillingPaymentTrackService.createOfflinePayment(newFormData).subscribe(
			(res) => {
				setTicketExternalId(res.ticketExternalId);
				setCreateOfflinePaymentStatus(CREATE_OFFLINE_PAYMENT_STATUS.DONE);
			},
			(error) => {
				setCreateOfflinePaymentStatus(CREATE_OFFLINE_PAYMENT_STATUS.ERROR);
			}
		);
	};

	const setPaymentDetailsField = (key: keyof IPaymentDetails, value: any) => {
		setPaymentDetails((paymentDetails) => ({
			...paymentDetails,
			[key]: value,
		}));
	};

	const isStepValid: boolean = useMemo(() => {
		switch (activeTab) {
			case STEPS.PAYMENT_TYPE_FORM:
				return !!paymentDetails.invoiceType && !!paymentDetails.bankAccountNumber && !!paymentDetails.bankCode;
			case STEPS.UPLOAD_EXCEL_FILE:
				return !!excelFileFormData.get(EXCEL_FILE);
			case STEPS.UPLOAD_PROOF_FILES:
				return !!proofFilesFormData.get(`${PROOF_FILES}1`);
			default:
				return false;
		}
	}, [activeTab, excelFileFormData, proofFilesFormData, paymentDetails]);

	const nextButtonDisabled: boolean = useMemo(() => {
		return !isStepValid;
	}, [isStepValid]);

	const nextFn = () => {
		switch (activeTab) {
			case STEPS.UPLOAD_PROOF_FILES:
				isStepValid && createOfflinePayment();
				break;
			default:
				if (isStepValid) {
					setActiveTab((activeTab) => activeTab + 1);
				}
				break;
		}
	};

	const backToPaymentTrack = () => history.push('/dashboard/payments/billing-payment-track');

	const backFn = () => {
		switch (activeTab) {
			case STEPS.PAYMENT_TYPE_FORM:
				backToPaymentTrack();
				break;
			default:
				setActiveTab((activeTab) => activeTab - 1);
				break;
		}
	};

	const hideCreateOfflinePaymentStatusDialog = () => {
		setCreateOfflinePaymentStatus(CREATE_OFFLINE_PAYMENT_STATUS.NONE);
		const subscription = refreshPayments();
		subscription.add(() => {
			history.push(`/${URL_PATHS.BILLING_PAYMENT_ROOT}`);
		});
	};

	return (
		<div className={tabsContainerClasses.container}>
			<BreadcrumbsItem to={match.path}>
				{`${translate('path.my.payments')} - ${translate('path.billing.payment.track')}`}
			</BreadcrumbsItem>
			<div className={classes.headingWrapper}>
				<HeadingMBold color="secondary-light">{translate('path.billing.payment.track')}</HeadingMBold>
			</div>
			<TabsWithDots
				tabs={[
					{
						name: translate('billing.payment.track.payment.details.form'),
						content: (
							<PaymentTypeForm
								invoiceType={paymentDetails.invoiceType}
								setInvoiceType={(value) => setPaymentDetailsField('invoiceType', value)}
								bankCode={paymentDetails.bankCode}
								bankAccountNumber={paymentDetails.bankAccountNumber}
								setBankCode={(value) => setPaymentDetailsField('bankCode', value)}
								setBankAccountNumber={(value) => setPaymentDetailsField('bankAccountNumber', value)}
								onClickBack={backToPaymentTrack}
							/>
						),
					},
					{
						name: translate('billing.payment.track.upload.excel.file'),
						content: (
							<UploadExcelFile
								invoiceType={paymentDetails.invoiceType}
								formData={excelFileFormData}
								setFormData={setExcelFileFFormData}
							/>
						),
					},
					{
						name: translate('billing.payment.upload.proof.files'),
						content: (
							<UploadFiles
								title={<HeadingSBold>{translate('billing.payment.upload.proof.files')}</HeadingSBold>}
								subtitle={<BodyM>{translate('billing.payment.upload.proof.files.reason')}</BodyM>}
								formData={proofFilesFormData}
								setFormData={setProofFilesFormData}
							/>
						),
					},
				]}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
			/>
			<div
				className={classNames({
					[classes.tabsButtons]: true,
					[classes.tabsButtonsIn]:
						activeTab === STEPS.PAYMENT_TYPE_FORM ||
						activeTab === STEPS.UPLOAD_EXCEL_FILE ||
						activeTab === STEPS.UPLOAD_PROOF_FILES,
				})}
			>
				<div className={classes.right}>
					<Button
						color="primary-light"
						className={classes.nextButton}
						disabled={nextButtonDisabled}
						onClick={nextFn}
					>
						{activeTab === STEPS.UPLOAD_PROOF_FILES ? translate('save') : translate('next')}
					</Button>
				</div>
				{activeTab !== STEPS.PAYMENT_TYPE_FORM && (
					<div className={classes.right}>
						<Button color="tertiary-light" onClick={backFn} disabled={false}>
							{translate('back')}
						</Button>
					</div>
				)}
			</div>
			{/* Check if Bank Muscat's new services are enabled */}
			<ConditionalRender
				show={isBankMuscatEnabled}
				onTrue={() => (
					<div className={classNames(classes.smallTab, classes.bannerWrapper)}>
						<PayWithBOMBanner />
					</div>
				)}
			/>
			<CreateOfflinePaymentStatusDialog
				isOpen={createOfflinePaymentStatus > 0}
				hideDialog={hideCreateOfflinePaymentStatusDialog}
				createOfflinePaymentStatus={createOfflinePaymentStatus}
				ticketExternalId={ticketExternalId}
			/>
		</div>
	);
};

export default RegisteringNewLoyalty;
